import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { apiFetch } from "../api/client";
import type { Assignment, SafeUser } from "../api/types";
import { useFetch } from "../hooks/useFetch";
import { ErrorBanner } from "../components/ErrorBanner";
import { EmptyState, LoadingState, PageHeader } from "../components/ui";

interface AssignmentsResponse {
  assignments: Assignment[];
}
interface UsersResponse {
  users: SafeUser[];
}

export function AssignmentsPage() {
  const params = useParams<{ testId: string }>();
  const testId = params.testId!;
  const assignments = useFetch<AssignmentsResponse>(
    `/admin/tests/${testId}/assignments`,
  );
  const users = useFetch<UsersResponse>("/admin/users");
  const [actionError, setActionError] = useState<Error | null>(null);
  const [classFilter, setClassFilter] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");

  const allStudents = useMemo(
    () => (users.data?.users ?? []).filter((u) => u.role === "student" && u.is_active),
    [users.data],
  );
  const assignedIds = useMemo(
    () => new Set((assignments.data?.assignments ?? []).map((a) => a.user_id)),
    [assignments.data],
  );

  const visibleClasses = useMemo(() => {
    const set = new Set<string>();
    for (const u of allStudents) {
      if (u.class_name) set.add(u.class_name);
    }
    return Array.from(set).sort();
  }, [allStudents]);

  const assignable = useMemo(
    () =>
      allStudents
        .filter((u) => !assignedIds.has(u.id))
        .filter((u) => !classFilter || u.class_name === classFilter),
    [allStudents, assignedIds, classFilter],
  );

  async function assign() {
    setActionError(null);
    if (!selectedUserId) return;
    try {
      await apiFetch(`/admin/tests/${testId}/assignments`, {
        method: "POST",
        body: JSON.stringify({ user_id: selectedUserId }),
      });
      setSelectedUserId("");
      assignments.refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e : new Error(String(e)));
    }
  }

  async function remove(userId: string) {
    setActionError(null);
    try {
      await apiFetch(`/admin/tests/${testId}/assignments/${userId}`, {
        method: "DELETE",
      });
      assignments.refetch();
    } catch (e) {
      setActionError(e instanceof Error ? e : new Error(String(e)));
    }
  }

  return (
    <section>
      <PageHeader
        title="Test assignments"
        subtitle="Only assigned students can open this test. Use the form below to add or remove students."
      />
      <ErrorBanner
        error={assignments.error ?? users.error ?? actionError}
      />

      <h2>Assigned students</h2>
      {assignments.loading ? (
        <LoadingState />
      ) : assignments.data && assignments.data.assignments.length > 0 ? (
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Class</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {assignments.data.assignments.map((a) => (
              <tr key={a.id}>
                <td>{a.user.username}</td>
                <td>{a.user.class_name ?? "—"}</td>
                <td>
                  <button
                    className="btn-danger"
                    onClick={() => void remove(a.user_id)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EmptyState
          title="No students assigned yet"
          hint="Pick a student from the dropdown below to assign them."
        />
      )}

      <h2>Assign student</h2>
      <div className="inline-form">
        <fieldset>
          <legend>Add</legend>
          <label>
            Class filter
            <select
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
            >
              <option value="">(any)</option>
              {visibleClasses.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label>
            Student
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">— select —</option>
              {assignable.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.username}
                  {u.class_name ? ` (${u.class_name})` : ""}
                </option>
              ))}
            </select>
          </label>
          <button onClick={() => void assign()} disabled={!selectedUserId}>
            Assign
          </button>
        </fieldset>
      </div>
    </section>
  );
}
